/**
 * Practitioner Hub → Events.
 *
 * Where a practitioner writes a community event, sends it for approval, and then runs it:
 * who is coming, who is waiting, what the tickets took, who turned up on the day.
 *
 * The Calendar itself is admin-owned, so nothing here reaches members until it is approved
 * (owner, Jun 2026). Wording can be edited freely afterwards; a new date, price or cap goes
 * back for review while the approved version stays live.
 */
import { useEffect, useState, useCallback } from "react";
import {
  CalendarPlus, MapPin, Link2, Users, Ticket, Clock, Mail, Check,
  AlertCircle, Trash2, Send, X, UserCheck, ChevronDown, ChevronUp,
} from "lucide-react";
import { apiFetch } from "../marketplace/api";

const BLANK = {
  title: "", description: "", event_date: "", event_time: "19:00",
  timezone: "America/Chicago", kind: "notice", venue_mode: "online",
  venue_address: "", link: "", capacity: "", waitlist_enabled: true,
  price: "", refund_policy: "day_7",
};

const STATUS_TEXT = {
  draft: "Not sent yet",
  pending: "Awaiting review",
  approved: "On the calendar",
  changes_requested: "Sent back to you",
  declined: "Not approved",
  cancelled: "Cancelled",
};

const styles = `
  .ev-root {
    --sand: #faf7f2; --teal: #1a5a44; --teal-mid: #24755a; --teal-bg: #eaf5f0;
    --white: #ffffff; --border: #e4ddd4; --ink: #1a1a16; --ink-mid: #5a5a54;
    --ink-light: #8a8a80; --amber-bg: #fdf6e3; --amber-border: #efdca8; --amber-ink: #7a5200;
    background: var(--sand); min-height: 100%; font-family: 'Source Sans 3', sans-serif;
  }
  .ev-header {
    background: var(--white); border-bottom: 1px solid var(--border);
    padding: 20px 28px; display: flex; justify-content: space-between;
    align-items: center; gap: 12px; flex-wrap: wrap;
  }
  .ev-header h1 { font-family: 'Literata', serif; font-size: 26px; color: var(--ink); margin: 0; }
  .ev-header p { font-size: 13px; color: var(--ink-mid); margin: 4px 0 0; }
  .ev-body { padding: 18px 28px 48px; display: flex; flex-direction: column; gap: 14px; }
  .ev-btn {
    font-size: 13px; font-weight: 600; padding: 8px 14px; border-radius: 8px;
    border: 1px solid var(--border); background: var(--white); color: var(--ink-mid);
    cursor: pointer; display: inline-flex; align-items: center; gap: 6px;
    transition: border-color 0.15s, color 0.15s, background 0.15s;
  }
  .ev-btn:hover { border-color: var(--teal); color: var(--ink); }
  .ev-btn:disabled { opacity: 0.5; cursor: not-allowed; }
  .ev-btn-primary { background: var(--teal); border-color: var(--teal); color: #fff; }
  .ev-btn-primary:hover { background: var(--teal-mid); border-color: var(--teal-mid); color: #fff; }
  .ev-btn-danger { border-color: #e8b0b0; color: #a02828; }
  .ev-btn-danger:hover { background: #fdf0f0; border-color: #a02828; color: #a02828; }
  .ev-card {
    background: var(--white); border: 1px solid var(--border); border-radius: 12px;
    padding: 16px 18px;
  }
  .ev-card h3 { font-family: 'Literata', serif; font-size: 18px; color: var(--ink); margin: 0; }
  .ev-chip {
    font-size: 11px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.5px;
    padding: 3px 9px; border-radius: 11px; border: 1px solid;
  }
  .ev-meta {
    display: flex; gap: 16px; flex-wrap: wrap; font-size: 13px;
    color: var(--ink-mid); margin-top: 8px;
  }
  .ev-meta span { display: flex; align-items: center; gap: 5px; }
  .ev-note {
    font-size: 13px; border-radius: 8px; padding: 10px 12px; margin-top: 10px;
    background: var(--amber-bg); border: 1px solid var(--amber-border); color: var(--amber-ink);
  }
  .ev-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(210px, 1fr)); gap: 12px; }
  .ev-field { display: flex; flex-direction: column; gap: 5px; }
  .ev-field label { font-size: 12px; font-weight: 600; color: var(--ink-mid); }
  .ev-field input, .ev-field select, .ev-field textarea {
    font-family: inherit; font-size: 14px; color: var(--ink); background: var(--white);
    border: 1px solid var(--border); border-radius: 8px; padding: 8px 10px; width: 100%;
  }
  .ev-field input:focus, .ev-field select:focus, .ev-field textarea:focus {
    outline: none; border-color: var(--teal);
  }
  .ev-check { display: flex; align-items: center; gap: 8px; font-size: 13px; color: var(--ink-mid); }
  .ev-attendee {
    display: flex; align-items: center; gap: 12px; flex-wrap: wrap;
    border-top: 1px solid var(--border); padding: 9px 0; font-size: 13px; color: var(--ink);
  }
  .ev-attendee:first-of-type { border-top: none; }
  .ev-attendee .ev-who { flex: 1; min-width: 160px; }
  .ev-attendee .ev-sub { font-size: 12px; color: var(--ink-light); }
  .ev-empty { text-align: center; padding: 34px 18px; color: var(--ink-mid); font-size: 14px; }
  .ev-stat { font-size: 13px; color: var(--ink-mid); }
  .ev-stat strong { color: var(--ink); font-size: 15px; }
`;

const chipStyle = (status) => ({
  approved:          { background: "#eaf5f0", color: "#1a5a44", borderColor: "#a0d8c4" },
  pending:           { background: "#fdf6e3", color: "#7a5200", borderColor: "#efdca8" },
  changes_requested: { background: "#eef4fd", color: "#1a50a0", borderColor: "#b0cce8" },
  declined:          { background: "#fdf0f0", color: "#a02828", borderColor: "#e8b0b0" },
  cancelled:         { background: "#f4f2ee", color: "#5a5a54", borderColor: "#e4ddd4" },
  draft:             { background: "#f4f2ee", color: "#5a5a54", borderColor: "#e4ddd4" },
}[status] || { background: "#f4f2ee", color: "#5a5a54", borderColor: "#e4ddd4" });

export default function PractitionerEventsPage() {
  const [events, setEvents] = useState([]);
  const [policies, setPolicies] = useState({});
  const [form, setForm] = useState(BLANK);
  const [editingId, setEditingId] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [saving, setSaving] = useState(false);
  const [notice, setNotice] = useState("");
  const [error, setError] = useState("");
  const [openPanel, setOpenPanel] = useState(null);

  const load = useCallback(async () => {
    try {
      const d = await apiFetch("/practitioner/events");
      setEvents(d?.events || []);
      setPolicies(d?.refund_policies || {});
    } catch {
      setEvents([]);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const set = (patch) => setForm((f) => ({ ...f, ...patch }));

  const startEdit = (ev) => {
    setEditingId(ev.id);
    setForm({
      ...BLANK, ...ev,
      capacity: ev.capacity ?? "",
      price: ev.price ? String(ev.price) : "",
    });
    setShowForm(true);
    setError("");
  };

  const save = async () => {
    setSaving(true);
    setError("");
    const payload = {
      ...form,
      capacity: form.capacity === "" ? null : Number(form.capacity),
      price: form.price === "" ? 0 : Number(form.price),
    };
    try {
      if (editingId) {
        await apiFetch(`/practitioner/events/${editingId}`, {
          method: "PUT", body: JSON.stringify(payload),
        });
        setNotice("Saved. Any change to the date, price or cap goes back for review.");
      } else {
        await apiFetch("/practitioner/events", {
          method: "POST", body: JSON.stringify(payload),
        });
        setNotice("Sent for review. You will hear back by email.");
      }
      setForm(BLANK); setEditingId(null); setShowForm(false);
      await load();
    } catch (e) {
      setError(e?.message || "That did not save.");
    }
    setSaving(false);
  };

  const act = async (id, path, body) => {
    try {
      await apiFetch(`/practitioner/events/${id}${path}`,
        { method: path === "" ? "DELETE" : "POST", body: body ? JSON.stringify(body) : undefined });
      await load();
    } catch (e) {
      setError(e?.message || "That did not go through.");
    }
  };

  const cancelEvent = async (ev) => {
    const paid = ev.kind === "ticketed";
    const msg = paid
      ? "Cancel this event? Every ticket is refunded in full, including the card fee, "
        + "and those fees are charged back to you."
      : "Cancel this event? It comes off the calendar and everyone coming is told.";
    if (!window.confirm(msg)) return;
    await act(ev.id, "/cancel");
    setNotice("Cancelled.");
  };

  const live = events.filter((e) => e.status === "approved").length;
  const waiting = events.filter((e) => e.status === "pending").length;

  return (
    <>
      <style>{styles}</style>
      <div className="ev-root" data-testid="practitioner-events">
        <div className="ev-header">
          <div>
            <h1>Events</h1>
            <p>{live} on the calendar · {waiting} awaiting review</p>
          </div>
          <button className="ev-btn ev-btn-primary" data-testid="event-new-btn"
            onClick={() => { setForm(BLANK); setEditingId(null); setShowForm((s) => !s); }}>
            <CalendarPlus size={14} /> {showForm && !editingId ? "Close" : "Add an event"}
          </button>
        </div>

        <div className="ev-body">
          {notice && (
            <div className="ev-card" style={{ background: "#eaf5f0", borderColor: "#a0d8c4" }}
              data-testid="event-notice">
              <span style={{ fontSize: 13, color: "#1a5a44" }}>{notice}</span>
            </div>
          )}
          {error && (
            <div className="ev-card" style={{ background: "#fdf0f0", borderColor: "#e8b0b0" }}
              data-testid="event-error">
              <span style={{ fontSize: 13, color: "#a02828" }}>{error}</span>
            </div>
          )}

          {showForm && (
            <div className="ev-card" data-testid="event-form">
              <h3>{editingId ? "Edit this event" : "A new event"}</h3>
              <p style={{ fontSize: 13, color: "#5a5a54", margin: "6px 0 14px" }}>
                It goes to the team for approval before members see it on the Calendar.
              </p>
              <div className="ev-grid">
                <div className="ev-field" style={{ gridColumn: "1 / -1" }}>
                  <label htmlFor="ev-title">What is it called</label>
                  <input id="ev-title" value={form.title} data-testid="event-title"
                    onChange={(e) => set({ title: e.target.value })}
                    placeholder="Full Moon Circle" />
                </div>
                <div className="ev-field" style={{ gridColumn: "1 / -1" }}>
                  <label htmlFor="ev-desc">What should people know</label>
                  <textarea id="ev-desc" rows={3} value={form.description}
                    data-testid="event-description"
                    onChange={(e) => set({ description: e.target.value })} />
                </div>
                <div className="ev-field">
                  <label htmlFor="ev-date">Date</label>
                  <input id="ev-date" type="date" value={form.event_date} data-testid="event-date"
                    onChange={(e) => set({ event_date: e.target.value })} />
                </div>
                <div className="ev-field">
                  <label htmlFor="ev-time">Time</label>
                  {/* A time INPUT, not free text: the calendar parses HH:MM and a typed
                      "7:00 PM" crashed the member Calendar outright (found in testing). */}
                  <input id="ev-time" type="time" value={form.event_time} data-testid="event-time"
                    onChange={(e) => set({ event_time: e.target.value })} />
                </div>
                <div className="ev-field">
                  <label htmlFor="ev-tz">Timezone</label>
                  <select id="ev-tz" value={form.timezone} data-testid="event-timezone"
                    onChange={(e) => set({ timezone: e.target.value })}>
                    {["America/New_York", "America/Chicago", "America/Denver",
                      "America/Los_Angeles", "America/Phoenix", "UTC"].map((tz) => (
                        <option key={tz} value={tz}>{tz.replace("America/", "").replace("_", " ")}</option>
                      ))}
                  </select>
                </div>
                <div className="ev-field">
                  <label htmlFor="ev-kind">What can people do</label>
                  <select id="ev-kind" value={form.kind} data-testid="event-kind"
                    onChange={(e) => set({ kind: e.target.value })}>
                    <option value="notice">Just read it</option>
                    <option value="rsvp">Say they're coming</option>
                    <option value="ticketed">Buy a ticket</option>
                  </select>
                </div>
                <div className="ev-field">
                  <label htmlFor="ev-mode">Where</label>
                  <select id="ev-mode" value={form.venue_mode} data-testid="event-venue-mode"
                    onChange={(e) => set({ venue_mode: e.target.value })}>
                    <option value="online">Online</option>
                    <option value="in_person">In person</option>
                  </select>
                </div>
                {form.venue_mode === "in_person" ? (
                  <div className="ev-field" style={{ gridColumn: "1 / -1" }}>
                    <label htmlFor="ev-addr">Address</label>
                    <input id="ev-addr" value={form.venue_address} data-testid="event-address"
                      onChange={(e) => set({ venue_address: e.target.value })}
                      placeholder="Street, town, state" />
                  </div>
                ) : (
                  <div className="ev-field" style={{ gridColumn: "1 / -1" }}>
                    <label htmlFor="ev-link">Link to join</label>
                    <input id="ev-link" value={form.link} data-testid="event-link"
                      onChange={(e) => set({ link: e.target.value })}
                      placeholder="https://" />
                  </div>
                )}
                {form.kind !== "notice" && (
                  <>
                    <div className="ev-field">
                      <label htmlFor="ev-cap">How many can come (optional)</label>
                      <input id="ev-cap" type="number" min="1" value={form.capacity}
                        data-testid="event-capacity"
                        onChange={(e) => set({ capacity: e.target.value })} />
                    </div>
                    <div className="ev-field">
                      <label className="ev-check" style={{ marginTop: 22 }}>
                        <input type="checkbox" checked={form.waitlist_enabled}
                          data-testid="event-waitlist"
                          onChange={(e) => set({ waitlist_enabled: e.target.checked })} />
                        Keep a waiting list once it is full
                      </label>
                    </div>
                  </>
                )}
                {form.kind === "ticketed" && (
                  <>
                    <div className="ev-field">
                      <label htmlFor="ev-price">Ticket price (USD)</label>
                      <input id="ev-price" type="number" min="1" step="0.01" value={form.price}
                        data-testid="event-price"
                        onChange={(e) => set({ price: e.target.value })} />
                    </div>
                    <div className="ev-field">
                      <label htmlFor="ev-refund">Refunds</label>
                      <select id="ev-refund" value={form.refund_policy} data-testid="event-refund"
                        onChange={(e) => set({ refund_policy: e.target.value })}>
                        {Object.entries(policies).map(([k, v]) => (
                          <option key={k} value={k}>{v}</option>
                        ))}
                      </select>
                    </div>
                  </>
                )}
              </div>
              {form.kind === "ticketed" && (
                <p className="ev-note" data-testid="event-ticket-note">
                  Ticket money goes straight into your own Stripe account, the same way session
                  payments do. If you cancel the event, every ticket is refunded in full including
                  the card fee, and those fees are charged back to you.
                </p>
              )}
              <div style={{ display: "flex", gap: 8, marginTop: 14, flexWrap: "wrap" }}>
                <button className="ev-btn ev-btn-primary" onClick={save} disabled={saving}
                  data-testid="event-save">
                  <Send size={14} /> {saving ? "Saving…" : editingId ? "Save" : "Send for approval"}
                </button>
                <button className="ev-btn" data-testid="event-cancel-form"
                  onClick={() => { setShowForm(false); setEditingId(null); setForm(BLANK); }}>
                  <X size={14} /> Cancel
                </button>
              </div>
            </div>
          )}

          {events.length === 0 && !showForm ? (
            <div className="ev-card ev-empty" data-testid="events-empty">
              <CalendarPlus size={26} style={{ opacity: 0.35, display: "block", margin: "0 auto 10px" }} />
              You have not put anything on the community Calendar yet.
            </div>
          ) : (
            events.map((ev) => (
              <div className="ev-card" key={ev.id} data-testid={`event-row-${ev.id}`}>
                <div style={{ display: "flex", justifyContent: "space-between", gap: 10, flexWrap: "wrap" }}>
                  <div>
                    <h3>{ev.title}</h3>
                    <div className="ev-meta">
                      <span><Clock size={13} /> {ev.event_date} at {ev.event_time}</span>
                      <span>
                        {ev.venue_mode === "in_person"
                          ? <><MapPin size={13} /> {ev.venue_address}</>
                          : <><Link2 size={13} /> {ev.link}</>}
                      </span>
                      {ev.kind === "ticketed" && (
                        <span><Ticket size={13} /> ${Number(ev.price || 0).toFixed(2)}</span>
                      )}
                      {ev.capacity ? <span><Users size={13} /> cap {ev.capacity}</span> : null}
                    </div>
                  </div>
                  <span className="ev-chip" style={chipStyle(ev.status)}
                    data-testid={`event-status-${ev.id}`}>
                    {STATUS_TEXT[ev.status] || ev.status}
                  </span>
                </div>

                {ev.admin_note && ev.status !== "approved" && (
                  <div className="ev-note" data-testid={`event-admin-note-${ev.id}`}>
                    <strong>From the team:</strong> {ev.admin_note}
                  </div>
                )}
                {ev.pending_changes && (
                  <div className="ev-note" data-testid={`event-change-pending-${ev.id}`}>
                    <AlertCircle size={13} style={{ display: "inline", marginRight: 5 }} />
                    Your change is waiting for approval. Until then the calendar shows the
                    version that was approved.
                  </div>
                )}

                <div style={{ display: "flex", gap: 8, marginTop: 12, flexWrap: "wrap" }}>
                  {ev.status !== "cancelled" && (
                    <button className="ev-btn" onClick={() => startEdit(ev)}
                      data-testid={`event-edit-${ev.id}`}>Edit</button>
                  )}
                  {(ev.status === "draft" || ev.status === "changes_requested") && (
                    <button className="ev-btn ev-btn-primary"
                      onClick={() => act(ev.id, "/submit")}
                      data-testid={`event-submit-${ev.id}`}>
                      <Send size={13} /> Send for approval
                    </button>
                  )}
                  {ev.kind !== "notice" && ev.status === "approved" && (
                    <button className="ev-btn"
                      onClick={() => setOpenPanel(openPanel === ev.id ? null : ev.id)}
                      data-testid={`event-attendees-toggle-${ev.id}`}>
                      <Users size={13} /> Who's coming
                      {openPanel === ev.id ? <ChevronUp size={13} /> : <ChevronDown size={13} />}
                    </button>
                  )}
                  {ev.status === "approved" && (
                    <button className="ev-btn ev-btn-danger" onClick={() => cancelEvent(ev)}
                      data-testid={`event-cancel-${ev.id}`}>Cancel event</button>
                  )}
                  {["draft", "declined", "changes_requested"].includes(ev.status) && (
                    <button className="ev-btn ev-btn-danger" onClick={() => act(ev.id, "")}
                      data-testid={`event-delete-${ev.id}`}>
                      <Trash2 size={13} /> Delete
                    </button>
                  )}
                </div>

                {openPanel === ev.id && <AttendeePanel event={ev} />}
              </div>
            ))
          )}
        </div>
      </div>
    </>
  );
}

function AttendeePanel({ event }) {
  const [data, setData] = useState(null);
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState("");

  const load = useCallback(async () => {
    try {
      setData(await apiFetch(`/practitioner/events/${event.id}/attendees`));
    } catch {
      setData({ attendees: [] });
    }
  }, [event.id]);

  useEffect(() => { load(); }, [load]);

  const toggleCheckIn = async (aid) => {
    await apiFetch(`/practitioner/events/${event.id}/attendees/${aid}/check-in`, { method: "POST" });
    load();
  };

  const promote = async (aid) => {
    try {
      await apiFetch(`/practitioner/events/${event.id}/attendees/${aid}/promote`, { method: "POST" });
      load();
    } catch (e) {
      alert(e?.message || "Could not move them up.");
    }
  };

  const sendEmail = async () => {
    setSending(true);
    try {
      const r = await apiFetch(`/practitioner/events/${event.id}/email`, {
        method: "POST", body: JSON.stringify({ subject, message }),
      });
      setSent(`Sent to ${r.sent} of ${r.of}.`);
      setSubject(""); setMessage("");
    } catch (e) {
      setSent(e?.message || "That did not send.");
    }
    setSending(false);
  };

  if (!data) return <p className="ev-stat" style={{ marginTop: 12 }}>Loading…</p>;

  const going = (data.attendees || []).filter((a) => a.state === "going");
  const waiting = (data.attendees || []).filter((a) => a.state === "waitlist");

  return (
    <div style={{ marginTop: 14, borderTop: "1px solid #e4ddd4", paddingTop: 12 }}
      data-testid={`event-attendees-${event.id}`}>
      <div style={{ display: "flex", gap: 22, flexWrap: "wrap", marginBottom: 10 }}>
        <span className="ev-stat"><strong>{data.going_count}</strong> coming</span>
        <span className="ev-stat"><strong>{data.waiting_count}</strong> waiting</span>
        {data.spots_left !== null && (
          <span className="ev-stat"><strong>{data.spots_left}</strong> places left</span>
        )}
        {event.kind === "ticketed" && (
          <span className="ev-stat">
            <strong>${Number(data.your_share || 0).toFixed(2)}</strong> yours
            {" "}(of ${Number(data.ticket_sales || 0).toFixed(2)} taken)
          </span>
        )}
      </div>

      {going.length === 0 && waiting.length === 0 ? (
        <p className="ev-stat" data-testid={`event-attendees-empty-${event.id}`}>
          Nobody yet.
        </p>
      ) : (
        <>
          {going.map((a) => (
            <div className="ev-attendee" key={a.id} data-testid={`attendee-${a.id}`}>
              <div className="ev-who">
                {a.name}
                <div className="ev-sub">{a.email}{a.paid_at ? " · paid" : ""}</div>
              </div>
              <button className="ev-btn" onClick={() => toggleCheckIn(a.id)}
                data-testid={`attendee-checkin-${a.id}`}
                style={a.checked_in_at
                  ? { background: "#eaf5f0", borderColor: "#a0d8c4", color: "#1a5a44" } : undefined}>
                {a.checked_in_at ? <><Check size={13} /> Here</> : <><UserCheck size={13} /> Check in</>}
              </button>
            </div>
          ))}
          {waiting.map((a) => (
            <div className="ev-attendee" key={a.id} data-testid={`attendee-${a.id}`}>
              <div className="ev-who">
                {a.name}
                <div className="ev-sub">{a.email} · waiting</div>
              </div>
              {event.kind !== "ticketed" && (
                <button className="ev-btn" onClick={() => promote(a.id)}
                  data-testid={`attendee-promote-${a.id}`}>Give them a place</button>
              )}
            </div>
          ))}
        </>
      )}

      {going.length > 0 && (
        <div style={{ marginTop: 14 }}>
          <div className="ev-field">
            <label htmlFor={`ev-sub-${event.id}`}>
              <Mail size={12} style={{ display: "inline", marginRight: 5 }} />
              Write to everyone coming
            </label>
            <input id={`ev-sub-${event.id}`} value={subject} placeholder="Subject"
              data-testid={`event-email-subject-${event.id}`}
              onChange={(e) => setSubject(e.target.value)} />
          </div>
          <div className="ev-field" style={{ marginTop: 8 }}>
            <textarea rows={3} value={message} placeholder="What do they need to know?"
              data-testid={`event-email-message-${event.id}`}
              onChange={(e) => setMessage(e.target.value)} />
          </div>
          <button className="ev-btn ev-btn-primary" style={{ marginTop: 8 }}
            disabled={sending || !subject.trim() || !message.trim()}
            onClick={sendEmail} data-testid={`event-email-send-${event.id}`}>
            <Send size={13} /> {sending ? "Sending…" : `Send to ${going.length}`}
          </button>
          {sent && <p className="ev-stat" style={{ marginTop: 8 }}
            data-testid={`event-email-result-${event.id}`}>{sent}</p>}
        </div>
      )}
    </div>
  );
}
